import type React from "react";
import { ExternalLink, Zap, Eye, Wand2 } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { ApiKeys } from "@/components/integration-hub/api-keys";
import { ApiUsage } from "@/components/integration-hub/api-usage";
import { PlatformIntegrations } from "@/components/integration-hub/platform-integrations";

const IntegrationHubPage: React.FC = () => {
  return (
    <>
      <main className="flex-1 space-y-6 p-2">
        <div className="space-y-2">
          <h2 className="text-3xl font-bold tracking-tight bg-gradient-to-r from-pink-500 to-purple-500 bg-clip-text text-transparent">
            Integration Hub
          </h2>
          <p className="text-muted-foreground">
            Manage API keys, monitor usage, and configure integrations.
          </p>
        </div>

        <Tabs defaultValue="api-management" className="space-y-6">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="api-management">API Management</TabsTrigger>
            {/* <TabsTrigger value="webhooks">Webhooks</TabsTrigger> */}
            <TabsTrigger value="platform-integrations">
              Platform Integrations
            </TabsTrigger>
          </TabsList>

          <TabsContent value="api-management" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>API Usage</CardTitle>
                <CardDescription>
                  Monitor your API usage and limits
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <ApiUsage />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>API Keys</CardTitle>
                <CardDescription>
                  Manage your API keys and access credentials
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <ApiKeys />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>API Documentation</CardTitle>
                <CardDescription>
                  Learn how to integrate with our APIs
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-6 md:grid-cols-2">
                  {[
                    {
                      name: "Youzu Lens API",
                      url: "https://docs.youzu.ai/docs/discovery/youzu-lens",
                      description: "Advanced visual discovery and product recognition",
                      icon: Eye,
                      category: "Discovery",
                      status: "stable"
                    },
                    {
                      name: "Room Visualizer API",
                      url: "https://docs.youzu.ai/docs/room-intelligence/room-visualizer",
                      description: "Virtual room staging and furniture placement",
                      icon: Wand2,
                      category: "Room Intelligence",
                      status: "stable"
                    },
                    {
                      name: "Youzu Swap API",
                      url: "https://docs.youzu.ai/docs/room-intelligence/youzu-swap",
                      description: "Furniture replacement and style transfer",
                      icon: Zap,
                      category: "Room Intelligence",
                      status: "beta"
                    },
                  ].map((api) => {
                    const IconComponent = api.icon;
                    return (
                      <Card key={api.name} className="group hover:shadow-lg transition-shadow duration-200">
                        <CardContent className="p-6">
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex items-center gap-3">
                              <div className="p-2 rounded-lg bg-gradient-to-br from-purple-100 to-pink-100">
                                <IconComponent className="h-5 w-5 text-purple-600" />
                              </div>
                              <div>
                                <h3 className="font-semibold text-gray-900 group-hover:text-purple-600 transition-colors">{api.name}</h3>
                                <p className="text-xs text-gray-500">{api.category}</p>
                              </div>
                            </div>
                            <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded-full">{api.status}</span>
                          </div>
                          
                          <p className="text-sm text-gray-600 mb-4">{api.description}</p>
                          
                          <a
                            href={api.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 text-sm font-medium text-purple-600 hover:text-purple-700 transition-colors"
                          >
                            View Documentation
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="platform-integrations" className="space-y-6">
            <PlatformIntegrations />
          </TabsContent>

          {/*       <TabsContent value="webhooks" className="space-y-6">
            <div className="grid gap-6 md:grid-cols-2">
              <div>
                <WebhookConfiguration />
              </div>

              <div>
                <WebhookHistory />
              </div>
            </div>
          </TabsContent> */}
        </Tabs>
      </main>
    </>
  );
};

export default IntegrationHubPage;
